import { AllocationResult } from '@oneview/api';
import {
    AccountDriftReportItem,
    AccountHistory,
    CaseSubmitData,
    DriftReportItem,
    OrderTransaction,
    OrderType,
    PositionType,
    PositionWithExtraInfo,
    Quote,
    RebalanceRecommendationItem,
    User,
} from '@orbis-systems/orbis-api-middleware';
import { InsightArticle } from '@orbis-systems/orbis-api-clients/insight';
import { add, divide, formatPhoneNumber, multiply, subtract, titleCase } from '@orbis-systems/orbis-react-toolkit';
import { find, get, orderBy } from 'lodash';

export interface AccountHistoryWithActivity extends AccountHistory {
    activity: string;
}

export function formatAccountHistory(data?: AccountHistory[]): AccountHistoryWithActivity[] {
    return (
        data?.map((item) => {
            const activities = Object.keys(item)
                .filter((key) => key !== 'addition' && get(item, key) === true)
                .map(titleCase);

            return {
                ...item,
                activity: activities.join(', '),
            };
        }) || []
    );
}

export function formatQuoteCombinedNews(
    symbol: string,
    passportNewsResponse: InsightArticle[],
    newsfeedResponse: InsightArticle[],
): InsightArticle[] {
    const filteredNewsFeed = newsfeedResponse.filter((item) => item.symbols.includes(symbol));

    const combinedData = passportNewsResponse.concat(filteredNewsFeed);

    return orderBy(
        combinedData.reverse(),
        [
            (item) => {
                return item.release_date;
            },
        ],
        'desc',
    );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapCaseValuesToData = (values: any, user: User) => {
    return {
        userId: user.userId,
        category: 'Ticket',
        ubdSubject: values.ubdSubject,
        bodyOfMessage: values.bodyOfMessage,
        cif: user.userId,
        status: 'New',
        phoneNumber: user.associatedPhone
            ? formatPhoneNumber(user.associatedPhone, false)
            : formatPhoneNumber(values.phoneNumber, false),
        emailRelatedToAccount: user.email ? user.email : values.email,
        brand: 'Axos',
    } as CaseSubmitData;
};

export interface OptionDetails {
    accountId: number;
    accountNumber: string;
    putOrCall: string;
    symbol: string;
    optionSymbol: string;
    equityPx: number;
    strike: number;
    inTheMoney: boolean;
    quantity: number;
    expDate: string;
}

export interface OptionDetailRaw {
    account: { accountId: number; accountNumber: string };
    price: number;
    quantity: number;
    quote: Quote;
    short: boolean;
    long: boolean;
}

export const formatOptionExpirations = (data: OptionDetailRaw[]) => {
    return data.map((item: OptionDetailRaw) => {
        return {
            accountId: item.account.accountId,
            accountNumber: item.account.accountNumber,
            longOrShort: item.long === true ? 'L' : item.short === true ? 'S' : '',
            putOrCall: item.quote.putOrCall,
            symbol: item.quote.underlying?.symbol,
            optionSymbol: item.quote.symbol,
            equityPx: item.quote.lastPrice,
            strike: item.quote.strikePrice,
            quantity: item.quantity,
            expDate: item.quote.expirationDate,
            quote: item.quote,
            inTheMoney: item.quote.inTheMoney,
        } as OptionDetails;
    });
};

export const getId = (data: DriftReportItem): string => `${data.symbol}.${data.type}`;

export type RebalanceRecommendationWithExtraInfo = RebalanceRecommendationItem & {
    type: PositionType;
    mutualFund: boolean;
    option: boolean;
    short: boolean;
    bond: boolean;
};

export const formatRebalanceRecommendation = (
    driftReport: AccountDriftReportItem[],
    recommendation: RebalanceRecommendationItem[],
): RebalanceRecommendationWithExtraInfo[] => {
    return recommendation.map((item) => {
        const symbol = item.symbol;
        const driftReportItem = find(driftReport, { symbol });

        return {
            ...item,
            type: driftReportItem?.type || PositionType.Cash,
            mutualFund: driftReportItem?.mutualFund || false,
            option: driftReportItem?.option || false,
            short: driftReportItem?.short || false,
            bond: driftReportItem?.bond || false,
        };
    });
};

export type PreviewDataAccountDriftReport = AccountDriftReportItem & {
    expectedPx: number;
    expectedQty: number;
    expectedValue: number;
    newQty: number;
    newWeight: number;
    newBalance: number;
    transaction: OrderTransaction;
    orderType?: OrderType;
    limitPrice?: number;
    stopPrice?: number;
    allocationRef?: string;
};

export type BasketOrderPreview = {
    expectedPx: number;
    expectedQty: number;
    quote: Quote;
};

export const formatPreviewDataAccountDrift = (
    driftReport: AccountDriftReportItem[],
    preAllocationData: AllocationResult[],
): PreviewDataAccountDriftReport[] => {
    const allocationSymbols = new Set(preAllocationData.map((item) => item.symbol));

    const filteredDriftReport = driftReport.filter((item) => allocationSymbols.has(item.symbol));

    return filteredDriftReport.map((item) => {
        const symbol = item.symbol;
        const allocation = preAllocationData.find((entry) => entry.symbol === symbol)!;
        const transaction =
            preAllocationData.find((item) => item.symbol === symbol)?.transaction || OrderTransaction.Buy;
        const { totalAllocationQty: expectedQty, quote } = allocation;
        const expectedPx = quote.lastPrice;
        const expectedValue = multiply(expectedPx, expectedQty);
        const isBuying = transaction === OrderTransaction.Buy;

        return {
            ...item,
            expectedPx,
            expectedQty,
            expectedValue,
            transaction,
            newQty: Math.abs(isBuying ? add(item.quantity, expectedQty) : subtract(item.quantity, expectedQty)),
            newWeight: Math.abs(
                isBuying
                    ? add(item.actual, divide(expectedValue, item.accountBalance))
                    : subtract(item.actual, divide(expectedValue, item.accountBalance)),
            ),
            newBalance: Math.abs(
                isBuying ? add(item.accountBalance, expectedValue) : subtract(item.accountBalance, expectedValue),
            ),
            quote,
            allocationRef: allocation.allocationRef,
        };
    });
};

export const getCanSellPosition = (positions: PositionWithExtraInfo[], symbol: string) =>
    positions.some((position) => !position.short && position.symbol === symbol);

export const getCanBuyToCoverPosition = (positions: PositionWithExtraInfo[], symbol: string) =>
    positions.some((position) => position.short && position.symbol === symbol);
